import React, {useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {Link, useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {graphQLServices} from "../../../services/GraphQLServices";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Breadcrumbs,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography
} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import {Allotment} from "allotment";
import "allotment/dist/style.css";
import {QuestionSetup} from "../models/definitions/QuestionSetup";
import {DynamicFormsSetupEditorData} from "../editor/DynamicFormsSetupEditorData";
import {QuestionComposer} from "../services/QuestionComposer";
import {ErrorBoundary} from "react-error-boundary";
import {generalError} from "../../fragments/ErrorElement";
import {QuestionFrontEndName, questionFrontEndNames} from "../models/definitions/QuestionFrontEndName";
import {QuestionClientName, questionClientNames} from "../models/definitions/QuestionClientName";
import {FormData, FormsRenderer} from "../services/FormsRenderer";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {ComposedQuestion} from "../models/definitions/ComposedQuestion";
import {GET_MULTIPLE_QUESTION_DETAILS, GET_QUESTION_DEFINITIONS, GET_QUESTION_DETAILS} from "./Queries";
import {localStorageOfString} from "../../../libraries/util/LocalStorage";
import {deepClone, normalizeData} from "../../../libraries/util/Functions";
import {CodeBlock} from "../../../components/common/CodeBlock";
import {DynamicFormsQuestionEditor} from "../editor/DynamicFormsEditor";
import {translateFunction} from "../../../localization/TranslationService";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";
import {GlobalState} from "../../../global/GlobalState";
import {dynamicFormsDefaultTargetStage} from "../DynamicFormsOverviewPage";
import {json} from "@codemirror/lang-json";

const translate = translateFunction("SureBasePortalDynamicForms");

export const newDefinition = localStorageOfString("dynamicForms:questionDetails:newDefinition");

function* distinctNeighbours<T>(array: T[]): IterableIterator<T> {
	let previous: T | undefined = undefined;
	for (const item of array) {
		if (item !== previous)
			yield item;
		previous = item;
	}
}

function DynamicFormsQuestionDetails() {
	const definition = useParams()?.id;
	
	const defaultTargetStage = "dev";
	const targetStage = GlobalState.targetStage ?? defaultTargetStage;
	const targetKiziStage = `kizi${targetStage}`;
	const disabled = targetStage !== defaultTargetStage;
	
	const questionDetails = useQuery(GET_QUESTION_DETAILS, {
		client: graphQLServices.dynamicFormsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: targetKiziStage,
			definition
		}
	});
	
	const questionDefinitions = useQuery(GET_QUESTION_DEFINITIONS, {
		client: graphQLServices.dynamicFormsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: targetKiziStage,
		}
	});
	
	if (questionDetails.loading || questionDefinitions.loading) {
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	if (questionDetails.error) {
		throw questionDetails.error;
	}
	if (questionDefinitions.error) {
		throw questionDefinitions.error;
	}
	
	if (!questionDetails.data || !questionDefinitions.data) {
		throw new Error("Missing data!");
	}
	
	const questions = newDefinition.get() === definition
		? [{definition, fullId: `${definition}:*:*`}]
		: questionDetails.data.questionSetups.nodes
			.map(deepClone)
			.map(normalizeData);
	if (questions.length === 0)
		throw new Error(`Question with id ${definition} is not found.`);
	
	const names = questionDefinitions.data.questionSetups.nodes.map((it: any) => it.definition);
	const distinctNames = [...distinctNeighbours(names)] as string[];
	
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={defaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}}
					to="/dynamicforms">{translate("Dynamic Forms")}</Link>
				<Link style={{color: "inherit", textDecoration: "unset"}}
					to="/dynamicforms/questions">{translate("Questions")}</Link>
				<Typography color="text.primary">{definition}</Typography>
			</Breadcrumbs>}
			center={<DynamicFormsQuestionDetailsData
				initialQuestions={questions}
				names={distinctNames}
				disabled={disabled}
			/>}
		/>
	</>;
}

function onlyUnique<T>(value: T, index: number, self: T[]): boolean {
	return self.indexOf(value) === index;
}

async function fetchDefinitions(definitions: string[]): Promise<QuestionSetup[]> {
	const response = await graphQLServices.dynamicFormsClient.query({
		query: GET_MULTIPLE_QUESTION_DETAILS,
		variables: {
			stage: GlobalState.targetKiziStage(dynamicFormsDefaultTargetStage),
			definitions
		}
	});
	
	const setups: QuestionSetup[] = response.data.questionSetups.nodes;
	
	return setups
		.map(it => deepClone(it))
		.map(it => normalizeData(it));
}

function DynamicFormsQuestionDetailsData(props: {
	initialQuestions: QuestionSetup[];
	names: string[];
	disabled: boolean;
}) {
	const [editorData, setEditorData] = useState(() => createEditorDataFromQuestionSetups(props.initialQuestions));
	
	const [extensions, setExtensions] = useState<QuestionSetup[] | undefined>();
	
	async function updateExtensions(setups: QuestionSetup[]) {
		const additions = setups
			.map(it => it.extends)
			.filter(it => it !== undefined && it !== null)
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			.map(it => it!)
			.filter(onlyUnique)
			.filter(extension => extensions === undefined || extensions.every(it => it.definition !== extension));
		
		if (additions.length > 0) {
			const setups2 = await fetchDefinitions(additions);
			setExtensions(extensions === undefined ? setups2 : extensions.concat(setups2));
		} else if (extensions === undefined) {
			setExtensions([]);
		}
		
	}
	
	function updateExtensionsFromData(data: DynamicFormsSetupEditorData<QuestionSetup>[]) {
		return updateExtensions(data.map(it => it.setup));
	}
	
	// noinspection JSIgnoredPromiseFromCall
	if (extensions === undefined)
		updateExtensionsFromData(editorData);
	
	async function updateEditorData(data: DynamicFormsSetupEditorData<QuestionSetup>) {
		let previous: DynamicFormsSetupEditorData<QuestionSetup> | null = null;
		const newData = editorData.length === 1
			? [data]
			: editorData.map(it => {
				const result = it.setup.fullId === data.setup.fullId
					? data
					: it.copy({
						parentData: previous
					});
				previous = result;
				return result;
			});
		setEditorData(newData);
		
		await updateExtensionsFromData(newData);
	}
	
	return <Allotment vertical={false}>
		<Allotment.Pane minSize={100}>
			<div style={{maxHeight: "100%", overflowY: "auto"}}>
				{editorData.map(data =>
					<Accordion key={data.setup.fullId} defaultExpanded={true}>
						<AccordionSummary
							expandIcon={<ExpandMore/>}
						>
							<Typography>{translate("Field")} {data.setup.fullId}</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<DynamicFormsQuestionEditor data={data} update={updateEditorData} names={props.names} disabled={props.disabled}/>
						</AccordionDetails>
					</Accordion>
				)}
			</div>
		</Allotment.Pane>
		<Allotment.Pane minSize={100}>
			<ErrorBoundary FallbackComponent={generalError} resetKeys={editorData}>
				<ComposedQuestionPreview setups={editorData.map(it => it.setup).concat(extensions ?? [])}/>
			</ErrorBoundary>
		</Allotment.Pane>
	</Allotment>;
}

function ComposedQuestionPreview(props: { setups: QuestionSetup[] }) {
	const setups = props.setups;
	
	const [frontEnd, setFrontEnd] = useState<QuestionFrontEndName>("kizi");
	const [client, setClient] = useState<QuestionClientName>("kizi");
	const [formData] = useState<FormData>(() => new FormData(null));
	
	const composer = new QuestionComposer();
	const renderer = new FormsRenderer();
	
	const composedQuestion = composer.composeQuestion(setups, frontEnd, client);
	const jsonCode = JSON.stringify(composedQuestion, null, 4);
	return <div style={{maxHeight: "100%", overflowY: "auto", overflowX: "auto"}}>
		<Stack
			direction={"row"}
			sx={{
				"& > :not(style)": {m: "5px", width: "200px"},
			}}>
			<FormControl>
				<InputLabel>{translate("Front-End Name")}</InputLabel>
				<Select
					value={frontEnd}
					label={translate("Front-End Name")}
					onChange={event => setFrontEnd(event.target.value as QuestionFrontEndName)}
				>
					{questionFrontEndNames.map(it =>
						<MenuItem key={it} value={it}>{translate(it)}</MenuItem>
					)}
				</Select>
			</FormControl>
			
			<FormControl>
				<InputLabel>{translate("Client Name")}</InputLabel>
				<Select
					value={client}
					label={translate("Client Name")}
					onChange={event => setClient(event.target.value as QuestionClientName)}
				>
					{questionClientNames.map(it =>
						<MenuItem key={it} value={it}>{translate(it)}</MenuItem>
					)}
				</Select>
			</FormControl>
		</Stack>
		
		<ErrorBoundary FallbackComponent={generalError}>
			<form onSubmit={event => event.preventDefault()}>
				<Stack
					alignItems={"flex-start"}
					sx={{
						"& > :not(style)": {p: "5px"},
					}}>
					{composedQuestion != null
						? <div>
							<DummyForm renderer={renderer} question={composedQuestion} formData={formData}/>
							<div><Button type="submit" variant="outlined">SEND</Button></div>
						</div>
						: undefined}
				</Stack>
			</form>
		</ErrorBoundary>
		
		<CodeBlock name={translate("composed-definition.json")} syntax={json()}>{jsonCode}</CodeBlock>
	</div>;
}

function DummyForm(props: { renderer: FormsRenderer; question: ComposedQuestion; formData: FormData }): JSX.Element {
	return props.renderer.renderQuestion(props.question, props.formData);
}

function createEditorDataFromQuestionSetups(initialQuestions: QuestionSetup[]): DynamicFormsSetupEditorData<QuestionSetup>[] {
	// parent order:
	// (setups are assumed to be in the correct order by the graphql query)
	// (which is true as long as the "definition" fields are the same and no separate overrides are present)
	// definition:*:*
	// definition:frontend:*
	// definition:*:client
	// definition:frontend:client
	
	const newDef = newDefinition.get();
	const isNew = (setup: QuestionSetup) => newDef === setup.definition;
	
	let previous: DynamicFormsSetupEditorData<QuestionSetup> | null = null;
	return initialQuestions.map(it => {
		const result = new DynamicFormsSetupEditorData(it, it, previous, false, isNew(it));
		previous = result;
		return result;
	});
}

export const DynamicFormsQuestionDetailsPage = pageAuthorizedByGroup(DynamicFormsQuestionDetails, ["Admin", "DynamicForms"]);

export function openQuestionDetailsForNewDefinition(definition: string) {
	newDefinition.set(definition);
	openQuestionDetailsForDefinition(definition);
}

export function openQuestionDetailsForDefinition(definition: string) {
	const trimmedId = definition.split(":")[0];
	window.location.href = `/dynamicforms/questions/${encodeURIComponent(trimmedId)}`;
}
